(() => {
  'use strict';

  /*
   * Pawkemon Product Squish
   *
   * Desktop:
   * - Plays once whenever the mouse enters a product card.
   * - Does not keep moving while the cursor remains over the card.
   *
   * Mobile:
   * - Waits until scrolling has stopped.
   * - Plays when at least 60% of a card is visible.
   * - Chooses the visible card closest to the viewport center.
   * - Each card plays only once per page load.
   *
   * Accessibility:
   * - Disabled when prefers-reduced-motion is enabled.
   */

  const SELECTORS = {
    card: [
      '.product-card-wrapper',
      '.card-wrapper'
    ].join(','),

    target: [
      '.card__inner',
      '.card__media'
    ].join(',')
  };

  const CLASS_NAMES = {
    target: 'pawkemon-squish-target',
    active: 'is-squishing'
  };

  const SETTINGS = {
    animationDuration: 760,
    scrollIdleDelay: 180,
    minimumVisibleRatio: 0.6,
    mobileMaximumWidth: 749
  };

  const reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const hoverQuery = window.matchMedia(
    '(hover: hover) and (pointer: fine)'
  );

  const mobileQuery = window.matchMedia(
    `(max-width: ${SETTINGS.mobileMaximumWidth}px)`
  );

  let observer = null;
  let scrollTimer = null;
  let animationLocked = false;
  let visibleCards = new Map();

  function motionIsDisabled() {
    return reducedMotionQuery.matches;
  }

  function desktopHoverIsAvailable() {
    return hoverQuery.matches;
  }

  function mobileModeIsActive() {
    return mobileQuery.matches && !desktopHoverIsAvailable();
  }

  function getTarget(card) {
    if (!card) return null;

    /*
     * Dawn normally uses .card__inner.
     * .card__media is kept as a safe fallback.
     */
    return (
      card.querySelector('.card__inner') ||
      card.querySelector('.card__media')
    );
  }

  function prepareCard(card) {
    if (!(card instanceof HTMLElement)) return null;

    const target = getTarget(card);
    if (!target) return null;

    target.classList.add(CLASS_NAMES.target);

    if (!card.dataset.pawkemonSquishReady) {
      card.dataset.pawkemonSquishReady = 'true';

      card.addEventListener('mouseenter', () => {
        if (motionIsDisabled()) return;
        if (!desktopHoverIsAvailable()) return;

        playSquish(card, false);
      });
    }

    return card;
  }

  function findCards(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') return [];

    return Array.from(root.querySelectorAll(SELECTORS.card))
      .map(prepareCard)
      .filter(Boolean);
  }

  function resetAnimation(target) {
    target.classList.remove(CLASS_NAMES.active);

    /*
     * Reading offsetWidth forces the browser to recognize the reset.
     * This lets desktop hover replay after the cursor leaves and re-enters.
     */
    void target.offsetWidth;
  }

  function playSquish(card, mobilePlay) {
    if (motionIsDisabled()) return false;
    if (!(card instanceof HTMLElement)) return false;
    if (animationLocked) return false;

    if (mobilePlay && card.dataset.pawkemonSquishPlayed === 'true') {
      return false;
    }

    const target = getTarget(card);
    if (!target) return false;

    animationLocked = true;

    if (mobilePlay) {
      card.dataset.pawkemonSquishPlayed = 'true';
    }

    resetAnimation(target);
    target.classList.add(CLASS_NAMES.active);

    const finish = () => {
      target.classList.remove(CLASS_NAMES.active);
      target.style.removeProperty('will-change');
      animationLocked = false;
    };

    target.addEventListener('animationend', finish, { once: true });

    /*
     * Fallback for browsers that fail to dispatch animationend,
     * or if another theme rule interrupts the animation.
     */
    window.setTimeout(() => {
      if (target.classList.contains(CLASS_NAMES.active)) {
        finish();
      }
    }, SETTINGS.animationDuration + 120);

    return true;
  }

  function distanceFromViewportCenter(card) {
    const rect = card.getBoundingClientRect();
    const cardCenter = rect.top + rect.height / 2;
    const viewportCenter = window.innerHeight / 2;

    return Math.abs(cardCenter - viewportCenter);
  }

  function playClosestVisibleCard() {
    if (!mobileModeIsActive()) return;
    if (motionIsDisabled()) return;
    if (animationLocked) return;

    const candidates = Array.from(visibleCards.entries())
      .filter(([card, ratio]) => {
        return (
          ratio >= SETTINGS.minimumVisibleRatio &&
          card.isConnected &&
          card.dataset.pawkemonSquishPlayed !== 'true'
        );
      })
      .map(([card]) => card)
      .sort((cardA, cardB) => {
        return (
          distanceFromViewportCenter(cardA) -
          distanceFromViewportCenter(cardB)
        );
      });

    const closestCard = candidates[0];

    if (closestCard) {
      playSquish(closestCard, true);
    }
  }

  function scheduleMobileSquish() {
    if (!mobileModeIsActive()) return;

    window.clearTimeout(scrollTimer);

    scrollTimer = window.setTimeout(() => {
      playClosestVisibleCard();
    }, SETTINGS.scrollIdleDelay);
  }

  function createObserver() {
    if (observer) {
      observer.disconnect();
    }

    visibleCards.clear();

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = entry.target;

          if (entry.isIntersecting) {
            visibleCards.set(card, entry.intersectionRatio);
          } else {
            visibleCards.delete(card);
          }
        });

        scheduleMobileSquish();
      },
      {
        threshold: [0, 0.25, 0.6, 0.75, 1],
        rootMargin: '-8% 0px -8% 0px'
      }
    );

    findCards().forEach((card) => observer.observe(card));
  }

  function initialize(root = document) {
    if (motionIsDisabled()) return;

    const cards = findCards(root);

    if (!observer) {
      createObserver();
      return;
    }

    cards.forEach((card) => observer.observe(card));
    scheduleMobileSquish();
  }

  function rebuildForViewportChange() {
    window.clearTimeout(scrollTimer);
    animationLocked = false;
    createObserver();
  }

  document.addEventListener('DOMContentLoaded', () => {
    initialize();

    window.addEventListener(
      'scroll',
      scheduleMobileSquish,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      scheduleMobileSquish,
      { passive: true }
    );
  });

  /*
   * Shopify Theme Editor reloads sections without refreshing the full page.
   * This reconnects the effect to newly loaded product cards.
   */
  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:reorder', () => {
    createObserver();
  });

  /*
   * Rebuild when a device changes orientation or input mode.
   */
  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', rebuildForViewportChange);
    hoverQuery.addEventListener('change', rebuildForViewportChange);

    reducedMotionQuery.addEventListener('change', () => {
      if (motionIsDisabled()) {
        if (observer) observer.disconnect();

        document
          .querySelectorAll(`.${CLASS_NAMES.active}`)
          .forEach((element) => {
            element.classList.remove(CLASS_NAMES.active);
          });
      } else {
        createObserver();
      }
    });
  }
})();